// frontend/src/pages/maintenance/AppointmentDetail.tsx

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  CircularProgress,
  IconButton,
  Sheet,
  Grid,
  AspectRatio,
  Modal,
  ModalDialog,
  ModalClose,
  FormControl,
  FormLabel,
  Input,
  Select,
  Option,
  Textarea,
  Switch,
  Autocomplete,
} from '@mui/joy';
import {
  ArrowLeft,
  MapPin,
  Clock,
  Camera,
  Wrench,
  FileText,
  DownloadSimple as Download,
  ArrowsClockwise as RefreshCw,
  CheckCircle,
  Warning as AlertTriangle,
  XCircle,
  Plus,
  PencilSimple as Edit,
  Trash as Trash2,
  UploadSimple as Upload,
} from '@phosphor-icons/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { appointmentApi, repairItemApi, photoApi } from '../../lib/maintenanceApi';
import { apiClient } from '../../lib/api';
import {
  APPOINTMENT_STATUS_LABELS,
  PHOTO_TYPE_LABELS,
  SEVERITY_LABELS,
  SEVERITY_COLORS,
  getStatusColor,
  formatDuration,
} from '../../types/maintenance';
import type {
  MaintenancePhoto,
  MaintenanceRepairItem,
  AppointmentStatus,
  RepairSeverity,
  PhotoType,
} from '../../types/maintenance';

const AppointmentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddRepairItemOpen, setIsAddRepairItemOpen] = useState(false);
  const [editingRepairItem, setEditingRepairItem] = useState<MaintenanceRepairItem | null>(null);
  const [isAddPhotoOpen, setIsAddPhotoOpen] = useState(false);

  // Fetch appointment
  const { data: appointment, isLoading } = useQuery({
    queryKey: ['maintenance-appointment', id],
    queryFn: () => appointmentApi.get(Number(id)),
    enabled: !!id,
  });

  // Regenerate report mutation
  const regenerateReportMutation = useMutation({
    mutationFn: () => appointmentApi.regenerateReport(Number(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-appointment', id] });
    },
  });

  // Create estimate mutation
  const createEstimateMutation = useMutation({
    mutationFn: () => appointmentApi.createEstimate(Number(id)),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-appointment', id] });
      // Redirect to the newly created estimate
      navigate(`/operations/billing/estimates/${data.estimate_id}`);
    },
  });

  // Update appointment mutation
  const updateAppointmentMutation = useMutation({
    mutationFn: (data: Partial<{ status: AppointmentStatus; scheduled_date: string; work_summary: string; assigned_to_id: number | null }>) =>
      appointmentApi.update(Number(id), data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-appointment', id] });
      setIsEditModalOpen(false);
    },
  });

  // Add repair item mutation
  const addRepairItemMutation = useMutation({
    mutationFn: (data: {
      title: string;
      description: string;
      location: string;
      system: string;
      solution: string;
      severity: RepairSeverity;
      estimated_cost?: string;
      repaired_on_site: boolean;
    }) => appointmentApi.addRepairItem(Number(id), data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-appointment', id] });
      setIsAddRepairItemOpen(false);
    },
  });

  // Update repair item mutation
  const updateRepairItemMutation = useMutation({
    mutationFn: ({ itemId, data }: { itemId: number; data: {
      title: string;
      description: string;
      location: string;
      system: string;
      solution: string;
      severity: RepairSeverity;
      estimated_cost?: string;
      repaired_on_site: boolean;
    }}) => repairItemApi.update(itemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-appointment', id] });
      setEditingRepairItem(null);
    },
  });

  // Delete repair item mutation
  const deleteRepairItemMutation = useMutation({
    mutationFn: (itemId: number) => repairItemApi.delete(itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-appointment', id] });
    },
  });

  // Add photo mutation
  const addPhotoMutation = useMutation({
    mutationFn: (data: { file: File; photo_type: PhotoType; description?: string }) => {
      const formData = new FormData();
      formData.append('image', data.file);
      formData.append('photo_type', data.photo_type);
      if (data.description) {
        formData.append('description', data.description);
      }
      return appointmentApi.addPhoto(Number(id), formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-appointment', id] });
      setIsAddPhotoOpen(false);
    },
  });

  // Delete photo mutation
  const deletePhotoMutation = useMutation({
    mutationFn: (photoId: number) => photoApi.delete(photoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-appointment', id] });
    },
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!appointment) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography level="h4">Appointment not found</Typography>
      </Box>
    );
  }

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'green':
        return <CheckCircle size={16} color={SEVERITY_COLORS.green} />;
      case 'yellow':
        return <AlertTriangle size={16} color={SEVERITY_COLORS.yellow} />;
      case 'red':
        return <XCircle size={16} color={SEVERITY_COLORS.red} />;
      default:
        return null;
    }
  };

  const hasRepairItemsForEstimate = appointment.repair_items.some(
    (item: MaintenanceRepairItem) => !item.repaired_on_site && item.estimated_cost
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate(`/maintenance/contracts/${appointment.contract.id}`)} variant="plain">
          <ArrowLeft />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography level="h2">
            Appointment - {appointment.scheduled_date}
          </Typography>
          <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
            {appointment.contract.contact_name}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="outlined"
            color="primary"
            startDecorator={<Edit size={16} />}
            onClick={() => setIsEditModalOpen(true)}
          >
            Edit
          </Button>
          <Chip
            size="lg"
            sx={{
              bgcolor: `${getStatusColor(appointment.status)}20`,
              color: getStatusColor(appointment.status),
            }}
          >
            {APPOINTMENT_STATUS_LABELS[appointment.status]}
          </Chip>
        </Stack>
      </Box>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
        {/* Main Content */}
        <Box sx={{ flex: 2 }}>
          {/* Visit Details */}
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography level="title-lg" sx={{ mb: 2 }}>
                <Clock size={18} style={{ marginRight: 8 }} />
                Visit Details
              </Typography>
              <Grid container spacing={2}>
                <Grid xs={6}>
                  <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                    Check-in Time
                  </Typography>
                  <Typography level="body-md">
                    {appointment.checkin_time
                      ? new Date(appointment.checkin_time).toLocaleString()
                      : 'Not checked in'}
                  </Typography>
                </Grid>
                <Grid xs={6}>
                  <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                    Checkout Time
                  </Typography>
                  <Typography level="body-md">
                    {appointment.checkout_time
                      ? new Date(appointment.checkout_time).toLocaleString()
                      : 'Not checked out'}
                  </Typography>
                </Grid>
                <Grid xs={6}>
                  <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                    Duration
                  </Typography>
                  <Typography level="body-md">
                    {formatDuration(appointment.duration_minutes)}
                  </Typography>
                </Grid>
                <Grid xs={6}>
                  <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                    Technician
                  </Typography>
                  <Typography level="body-md">
                    {appointment.assigned_to
                      ? `${appointment.assigned_to.first_name} ${appointment.assigned_to.last_name}`
                      : 'Unassigned'}
                  </Typography>
                </Grid>
              </Grid>
              {appointment.work_summary && (
                <Box sx={{ mt: 2 }}>
                  <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                    Work Summary
                  </Typography>
                  <Typography level="body-md">{appointment.work_summary}</Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Repair Items */}
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography level="title-lg">
                  <Wrench size={18} style={{ marginRight: 8 }} />
                  Repair Items ({appointment.repair_items.length})
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="sm"
                    variant="soft"
                    startDecorator={<Plus size={16} />}
                    onClick={() => setIsAddRepairItemOpen(true)}
                  >
                    Add Item
                  </Button>
                  {hasRepairItemsForEstimate && (
                    <Button
                      size="sm"
                      variant="soft"
                      color="primary"
                      startDecorator={<FileText size={16} />}
                      onClick={() => createEstimateMutation.mutate()}
                      loading={createEstimateMutation.isPending}
                    >
                      Create Estimate
                    </Button>
                  )}
                </Stack>
              </Box>
              {appointment.repair_items.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Wrench size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
                  <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                    No repair items found
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={2}>
                  {appointment.repair_items.map((item: MaintenanceRepairItem) => (
                    <Sheet key={item.id} variant="outlined" sx={{ p: 2, borderRadius: 'sm' }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        {getSeverityIcon(item.severity)}
                        <Box sx={{ flex: 1 }}>
                          <Typography level="title-sm">{item.title}</Typography>
                          {item.location && (
                            <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                              {item.location}
                            </Typography>
                          )}
                          {item.description && (
                            <Typography level="body-sm" sx={{ mt: 1 }}>
                              {item.description}
                            </Typography>
                          )}
                          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                            <Chip
                              size="sm"
                              sx={{
                                bgcolor: `${SEVERITY_COLORS[item.severity]}20`,
                                color: SEVERITY_COLORS[item.severity],
                              }}
                            >
                              {SEVERITY_LABELS[item.severity]}
                            </Chip>
                            {item.estimated_cost && (
                              <Chip size="sm" variant="soft">
                                Est: ${item.estimated_cost}
                              </Chip>
                            )}
                            {item.repaired_on_site && (
                              <Chip size="sm" color="success" variant="soft">
                                Repaired On Site
                              </Chip>
                            )}
                          </Stack>
                        </Box>
                        <Stack direction="row" spacing={0.5}>
                          <IconButton
                            size="sm"
                            variant="plain"
                            color="neutral"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingRepairItem(item);
                            }}
                          >
                            <Edit size={16} />
                          </IconButton>
                          <IconButton
                            size="sm"
                            variant="plain"
                            color="danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (confirm('Delete this repair item?')) {
                                deleteRepairItemMutation.mutate(item.id);
                              }
                            }}
                          >
                            <Trash2 size={16} />
                          </IconButton>
                        </Stack>
                      </Box>
                    </Sheet>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>

          {/* Photos */}
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography level="title-lg">
                  <Camera size={18} style={{ marginRight: 8 }} />
                  Photos ({appointment.photos.length})
                </Typography>
                <Button
                  size="sm"
                  variant="soft"
                  startDecorator={<Plus size={16} />}
                  onClick={() => setIsAddPhotoOpen(true)}
                >
                  Add Photo
                </Button>
              </Box>
              {appointment.photos.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Camera size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
                  <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                    No photos taken
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {appointment.photos.map((photo: MaintenancePhoto) => (
                    <Grid key={photo.id} xs={6} sm={4} md={3}>
                      <Card variant="outlined" sx={{ position: 'relative' }}>
                        <IconButton
                          size="sm"
                          variant="soft"
                          color="danger"
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            zIndex: 1,
                          }}
                          onClick={() => {
                            if (confirm('Delete this photo?')) {
                              deletePhotoMutation.mutate(photo.id);
                            }
                          }}
                        >
                          <Trash2 size={14} />
                        </IconButton>
                        <AspectRatio ratio="1">
                          <img
                            src={photo.thumbnail_url || photo.image_url}
                            alt={photo.description || 'Maintenance photo'}
                            style={{ objectFit: 'cover' }}
                          />
                        </AspectRatio>
                        <CardContent sx={{ p: 1 }}>
                          <Chip size="sm" variant="soft">
                            {PHOTO_TYPE_LABELS[photo.photo_type]}
                          </Chip>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Sidebar */}
        <Box sx={{ flex: 1 }}>
          {/* Contract Info */}
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography level="title-md" sx={{ mb: 2 }}>
                Contract
              </Typography>
              <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                {appointment.contract.contract_number}
              </Typography>
              <Button
                size="sm"
                variant="plain"
                sx={{ mt: 1, p: 0 }}
                onClick={() => navigate(`/maintenance/contracts/${appointment.contract.id}`)}
              >
                View Contract
              </Button>
            </CardContent>
          </Card>

          {/* Property */}
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography level="title-md" sx={{ mb: 2 }}>
                <MapPin size={16} style={{ marginRight: 8 }} />
                Property
              </Typography>
              <Typography level="body-sm">
                {appointment.contract.full_property_address}
              </Typography>
            </CardContent>
          </Card>

          {/* Report */}
          <Card variant="outlined">
            <CardContent>
              <Typography level="title-md" sx={{ mb: 2 }}>
                <FileText size={16} style={{ marginRight: 8 }} />
                Report
              </Typography>
              {appointment.report_generated ? (
                <Stack spacing={1}>
                  <Typography level="body-sm" sx={{ color: 'success.600' }}>
                    <CheckCircle size={14} style={{ marginRight: 4 }} />
                    Report generated
                  </Typography>
                  {appointment.report_file && (
                    <Button
                      size="sm"
                      variant="soft"
                      startDecorator={<Download size={16} />}
                      component="a"
                      href={appointment.report_file}
                      target="_blank"
                    >
                      Download PDF
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="plain"
                    startDecorator={<RefreshCw size={16} />}
                    onClick={() => regenerateReportMutation.mutate()}
                    loading={regenerateReportMutation.isPending}
                  >
                    Regenerate
                  </Button>
                </Stack>
              ) : (
                <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                  Report not yet generated
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      </Stack>

      {/* Edit Appointment Modal */}
      <EditAppointmentModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        appointment={appointment}
        onSave={(data) => updateAppointmentMutation.mutate(data)}
        isLoading={updateAppointmentMutation.isPending}
      />

      {/* Add Repair Item Modal */}
      <RepairItemModal
        open={isAddRepairItemOpen}
        onClose={() => setIsAddRepairItemOpen(false)}
        onSave={(data) => addRepairItemMutation.mutate(data)}
        isLoading={addRepairItemMutation.isPending}
      />

      {/* Edit Repair Item Modal */}
      <RepairItemModal
        open={!!editingRepairItem}
        onClose={() => setEditingRepairItem(null)}
        onSave={(data) => {
          if (editingRepairItem) {
            updateRepairItemMutation.mutate({ itemId: editingRepairItem.id, data });
          }
        }}
        isLoading={updateRepairItemMutation.isPending}
        editItem={editingRepairItem}
      />

      {/* Add Photo Modal */}
      <AddPhotoModal
        open={isAddPhotoOpen}
        onClose={() => setIsAddPhotoOpen(false)}
        onSave={(data) => addPhotoMutation.mutate(data)}
        isLoading={addPhotoMutation.isPending}
      />
    </Box>
  );
};

// Edit Appointment Modal Component
interface TechnicianOption {
  id: number;
  first_name: string;
  last_name: string;
}

interface EditAppointmentModalProps {
  open: boolean;
  onClose: () => void;
  appointment: {
    status: AppointmentStatus;
    scheduled_date: string;
    work_summary: string | null;
    assigned_to: TechnicianOption | null;
  };
  onSave: (data: Partial<{ status: AppointmentStatus; scheduled_date: string; work_summary: string; assigned_to_id: number | null }>) => void;
  isLoading: boolean;
}

const EditAppointmentModal: React.FC<EditAppointmentModalProps> = ({
  open,
  onClose,
  appointment,
  onSave,
  isLoading,
}) => {
  const [status, setStatus] = useState<AppointmentStatus>(appointment.status);
  const [scheduledDate, setScheduledDate] = useState(appointment.scheduled_date);
  const [workSummary, setWorkSummary] = useState(appointment.work_summary || '');
  const [selectedTechnician, setSelectedTechnician] = useState<TechnicianOption | null>(appointment.assigned_to);

  // Fetch technicians
  const { data: techniciansData } = useQuery({
    queryKey: ['technicians'],
    queryFn: async () => {
      const response = await apiClient.get('/api/users/');
      return response.data as { results: TechnicianOption[] };
    },
  });

  const technicians = techniciansData?.results || [];

  React.useEffect(() => {
    setStatus(appointment.status);
    setScheduledDate(appointment.scheduled_date);
    setWorkSummary(appointment.work_summary || '');
    setSelectedTechnician(appointment.assigned_to);
  }, [appointment]);

  const handleSubmit = () => {
    onSave({
      status,
      scheduled_date: scheduledDate,
      work_summary: workSummary || undefined,
      assigned_to_id: selectedTechnician?.id || null,
    });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ width: 450, maxWidth: '95vw' }}>
        <ModalClose />
        <Typography level="h4">Edit Appointment</Typography>

        <Stack spacing={2} sx={{ mt: 2 }}>
          <FormControl>
            <FormLabel>Status</FormLabel>
            <Select
              value={status}
              onChange={(_, value) => setStatus(value as AppointmentStatus)}
            >
              {Object.entries(APPOINTMENT_STATUS_LABELS).map(([value, label]) => (
                <Option key={value} value={value}>
                  {label}
                </Option>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel>Assigned Technician</FormLabel>
            <Autocomplete
              placeholder="Select technician..."
              options={technicians}
              getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
              value={selectedTechnician}
              onChange={(_, value) => setSelectedTechnician(value)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Scheduled Date</FormLabel>
            <Input
              type="date"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Work Summary</FormLabel>
            <Textarea
              minRows={3}
              placeholder="Describe the work performed..."
              value={workSummary}
              onChange={(e) => setWorkSummary(e.target.value)}
            />
          </FormControl>

          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button variant="plain" color="neutral" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={isLoading}>
              Save Changes
            </Button>
          </Stack>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};

// Repair Item Modal Component (Add/Edit)
interface RepairItemModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: {
    title: string;
    description: string;
    location: string;
    system: string;
    solution: string;
    severity: RepairSeverity;
    estimated_cost?: string;
    repaired_on_site: boolean;
  }) => void;
  isLoading: boolean;
  editItem?: MaintenanceRepairItem | null;
}

const RepairItemModal: React.FC<RepairItemModalProps> = ({
  open,
  onClose,
  onSave,
  isLoading,
  editItem,
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [system, setSystem] = useState('');
  const [solution, setSolution] = useState('');
  const [severity, setSeverity] = useState<RepairSeverity>('green');
  const [estimatedCost, setEstimatedCost] = useState('');
  const [repairedOnSite, setRepairedOnSite] = useState(false);

  const isEditing = !!editItem;

  // Populate form when editing
  React.useEffect(() => {
    if (editItem) {
      setTitle(editItem.title || '');
      setDescription(editItem.description || '');
      setLocation(editItem.location || '');
      setSystem(editItem.system || '');
      setSolution(editItem.solution || '');
      setSeverity(editItem.severity || 'green');
      setEstimatedCost(editItem.estimated_cost?.toString() || '');
      setRepairedOnSite(editItem.repaired_on_site || false);
    }
  }, [editItem]);

  const handleSubmit = () => {
    if (!title) return;
    onSave({
      title,
      description: description || '',
      location: location || '',
      system: system || '',
      solution: solution || '',
      severity,
      estimated_cost: estimatedCost || undefined,
      repaired_on_site: repairedOnSite,
    });
  };

  const handleClose = () => {
    setTitle('');
    setDescription('');
    setLocation('');
    setSystem('');
    setSolution('');
    setSeverity('green');
    setEstimatedCost('');
    setRepairedOnSite(false);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog sx={{ width: 500, maxWidth: '95vw' }}>
        <ModalClose />
        <Typography level="h4">{isEditing ? 'Edit Repair Item' : 'Add Repair Item'}</Typography>

        <Stack spacing={2} sx={{ mt: 2 }}>
          <FormControl required>
            <FormLabel>Title</FormLabel>
            <Input
              placeholder="e.g., Damaged shingle"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Location</FormLabel>
            <Input
              placeholder="e.g., North side of roof"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </FormControl>

          <FormControl>
            <FormLabel>System</FormLabel>
            <Input
              placeholder="e.g., Roofing, Gutters, Flashing"
              value={system}
              onChange={(e) => setSystem(e.target.value)}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Description</FormLabel>
            <Textarea
              minRows={2}
              placeholder="Detailed description of the issue..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Solution</FormLabel>
            <Textarea
              minRows={2}
              placeholder="Recommended solution for the issue..."
              value={solution}
              onChange={(e) => setSolution(e.target.value)}
            />
          </FormControl>

          <FormControl required>
            <FormLabel>Severity</FormLabel>
            <Select
              value={severity}
              onChange={(_, value) => setSeverity(value as RepairSeverity)}
            >
              {Object.entries(SEVERITY_LABELS).map(([value, label]) => (
                <Option key={value} value={value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: SEVERITY_COLORS[value as RepairSeverity],
                      }}
                    />
                    {label}
                  </Box>
                </Option>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel>Estimated Cost</FormLabel>
            <Input
              type="number"
              startDecorator="$"
              placeholder="0.00"
              value={estimatedCost}
              onChange={(e) => setEstimatedCost(e.target.value)}
            />
          </FormControl>

          <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
            <FormLabel>Repaired On Site</FormLabel>
            <Switch
              checked={repairedOnSite}
              onChange={(e) => setRepairedOnSite(e.target.checked)}
            />
          </FormControl>

          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button variant="plain" color="neutral" onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={isLoading} disabled={!title}>
              {isEditing ? 'Save Changes' : 'Add Item'}
            </Button>
          </Stack>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};

// Add Photo Modal Component
interface AddPhotoModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: { file: File; photo_type: PhotoType; description?: string }) => void;
  isLoading: boolean;
}

const AddPhotoModal: React.FC<AddPhotoModalProps> = ({
  open,
  onClose,
  onSave,
  isLoading,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [photoType, setPhotoType] = useState<PhotoType>('general');
  const [description, setDescription] = useState('');
  const [preview, setPreview] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleSubmit = () => {
    if (!file) return;
    onSave({
      file,
      photo_type: photoType,
      description: description || undefined,
    });
  };

  const handleClose = () => {
    setFile(null);
    setPhotoType('general');
    setDescription('');
    setPreview(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog sx={{ width: 450, maxWidth: '95vw' }}>
        <ModalClose />
        <Typography level="h4">Add Photo</Typography>

        <Stack spacing={2} sx={{ mt: 2 }}>
          <FormControl required>
            <FormLabel>Photo</FormLabel>
            <Button
              component="label"
              variant="outlined"
              color="primary"
              startDecorator={<Upload size={18} />}
              sx={{ justifyContent: 'flex-start' }}
            >
              {file ? file.name : 'Choose file...'}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleFileChange}
              />
            </Button>
          </FormControl>

          {preview && (
            <AspectRatio ratio="16/9" sx={{ borderRadius: 'sm', overflow: 'hidden' }}>
              <img src={preview} alt="Preview" style={{ objectFit: 'cover' }} />
            </AspectRatio>
          )}

          <FormControl required>
            <FormLabel>Photo Type</FormLabel>
            <Select
              value={photoType}
              onChange={(_, value) => setPhotoType(value as PhotoType)}
            >
              {Object.entries(PHOTO_TYPE_LABELS).map(([value, label]) => (
                <Option key={value} value={value}>
                  {label}
                </Option>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel>Description</FormLabel>
            <Textarea
              minRows={2}
              placeholder="Optional description..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </FormControl>

          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button variant="plain" color="neutral" onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={isLoading} disabled={!file}>
              Upload Photo
            </Button>
          </Stack>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};

export default AppointmentDetail;
